import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, X, Share2, Link2, Star, ZoomIn, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatBRL, resolveImageUrl } from "../lib/api";
import { useCart } from "../context/CartContext";
import { WhatsAppIcon } from "../components/store/Header";
import CartDrawer from "../components/store/CartDrawer";
import Header from "../components/store/Header";

export default function ProductDetail() {
  const { id } = useParams();
  const { addItem, openCart } = useCart();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [settings, setSettings] = useState({ store_name: "Dra.Cell", whatsapp_number: "", logo_url: "" });
  const [reviews, setReviews] = useState([]);
  const [index, setIndex] = useState(0);
  const [zoomed, setZoomed] = useState(false);
  const touchStartX = useRef(0);

  useEffect(() => {
    api.get(`/products/${id}`).then((res) => setProduct(res.data)).catch(() => setNotFound(true));
    api.get("/settings").then((res) => setSettings(res.data)).catch(() => {});
    api.get(`/reviews?product_id=${id}`).then((res) => setReviews(res.data)).catch(() => {});
    window.scrollTo(0, 0);
  }, [id]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-[var(--site-bg)] flex flex-col items-center justify-center gap-4 px-6 text-center">
        <p className="font-display font-bold text-2xl">Produto não encontrado</p>
        <Link to="/" className="text-sm text-[var(--brand)] hover:underline">← Voltar para a loja</Link>
      </div>
    );
  }
  if (!product) {
    return <div className="min-h-screen bg-[var(--site-bg)] flex items-center justify-center text-sm text-[#6B645C]">Carregando...</div>;
  }

  const photos = product.photos?.length ? product.photos : [product.image_url].filter(Boolean);
  const hasPromo = product.promo_price && product.promo_price < product.price;
  const effectivePrice = hasPromo ? product.promo_price : product.price;
  const outOfStock = (product.stock !== null && product.stock !== undefined && product.stock <= 0) || product.status === "indisponivel";
  const shareUrl = `${process.env.REACT_APP_BACKEND_URL}/api/p/${product.id}`;

  const prev = () => setIndex((i) => (i - 1 + photos.length) % photos.length);
  const next = () => setIndex((i) => (i + 1) % photos.length);

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 40) (delta > 0 ? prev : next)();
  };

  const shareWhatsApp = () => {
    const text = `Olha esse produto na ${settings.store_name}: ${product.name} — ${formatBRL(effectivePrice)}\n${shareUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copiado!");
    } catch {
      toast.error("Não consegui copiar. Copie o endereço da página.");
    }
  };

  return (
    <div className="bg-[var(--site-bg)] text-[#1A1816] min-h-screen antialiased">
      <Header storeName={settings.store_name} logoUrl={settings.logo_url} />
      <main className="max-w-6xl mx-auto px-6 py-10">
        <Link to="/" data-testid="back-to-store" className="inline-flex items-center gap-2 text-sm text-[#5A534C] hover:text-[var(--brand)] transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" /> Voltar para a loja
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Galeria */}
          <div>
            <div
              className="relative aspect-square rounded-3xl overflow-hidden bg-[#EDE6DB] border border-[#E8E2D9] group"
              onTouchStart={onTouchStart}
              onTouchEnd={onTouchEnd}
              data-testid="product-gallery"
            >
              {photos.length > 0 ? (
                <img
                  src={resolveImageUrl(photos[index])}
                  alt={`${product.name} — foto ${index + 1}`}
                  className="absolute inset-0 w-full h-full object-contain bg-white cursor-zoom-in"
                  onClick={() => setZoomed(true)}
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-[#6B645C] font-mono text-xs uppercase">Sem foto</div>
              )}
              {photos.length > 1 && (
                <>
                  <button type="button" onClick={prev} data-testid="gallery-prev" aria-label="Foto anterior"
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 shadow flex items-center justify-center hover:bg-[var(--brand)] hover:text-white transition-colors">
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button type="button" onClick={next} data-testid="gallery-next" aria-label="Próxima foto"
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 shadow flex items-center justify-center hover:bg-[var(--brand)] hover:text-white transition-colors">
                    <ChevronRight className="w-5 h-5" />
                  </button>
                  <span className="absolute bottom-3 right-3 bg-black/60 text-white font-mono text-[11px] px-2.5 py-1 rounded-full">
                    {index + 1}/{photos.length}
                  </span>
                </>
              )}
              {photos.length > 0 && (
                <span className="absolute bottom-3 left-3 bg-black/60 text-white font-mono text-[11px] px-2.5 py-1 rounded-full inline-flex items-center gap-1">
                  <ZoomIn className="w-3 h-3" /> Toque para ampliar
                </span>
              )}
            </div>
            {photos.length > 1 && (
              <div className="flex gap-2 mt-3 overflow-x-auto" data-testid="gallery-thumbs">
                {photos.map((p, i) => (
                  <button key={i} type="button" onClick={() => setIndex(i)} data-testid={`gallery-thumb-${i}`}
                    className={`w-16 h-16 rounded-xl overflow-hidden border-2 shrink-0 transition-colors ${i === index ? "border-[var(--brand)]" : "border-[#E8E2D9]"}`}>
                    <img src={resolveImageUrl(p)} alt={`Miniatura ${i + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="flex flex-col">
            {product.badge && <span className="self-start bg-[#1A1816] text-[var(--site-bg)] text-[10px] font-mono uppercase px-2.5 py-1 rounded mb-3">{product.badge}</span>}
            <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight mb-2">{product.name}</h1>
            <p className="font-mono text-xs uppercase tracking-wider text-[#6B645C] mb-6">{product.category}</p>
            <div className="mb-6">
              {hasPromo && <p className="font-mono text-sm text-[#A89E94] line-through">{formatBRL(product.price)}</p>}
              <p className="font-display font-bold text-4xl text-[#1A1816]" data-testid="product-price">{formatBRL(effectivePrice)}</p>
              {hasPromo && <span className="inline-block mt-1 bg-[var(--brand)] text-white text-[11px] font-mono uppercase px-2.5 py-1 rounded-full">Promoção</span>}
            </div>
            <p className="text-[#5A534C] leading-relaxed mb-8">{product.description}</p>

            <div className="flex flex-col gap-3 mb-8">
              <button
                type="button"
                disabled={outOfStock}
                onClick={() => { addItem({ ...product, price: effectivePrice }); openCart(); }}
                data-testid="detail-add-to-cart"
                className="w-full bg-[var(--brand)] text-white py-4 rounded-full font-display text-sm font-semibold hover:bg-[var(--brand-dark)] transition-colors disabled:opacity-50"
              >
                {outOfStock ? "Produto Indisponível" : product.status === "sob_encomenda" ? "Encomendar na Sacola" : "Adicionar à Sacola"}
              </button>
              <a
                href={`https://wa.me/${(settings.whatsapp_number || "").replace(/\D/g, "")}?text=${encodeURIComponent(`Olá! Tenho interesse no produto: ${product.name} — ${formatBRL(effectivePrice)}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="detail-whatsapp-button"
                className="w-full inline-flex items-center justify-center gap-2 border border-[#D5CDC2] py-4 rounded-full font-display text-sm font-semibold hover:border-[#1A1816] transition-colors"
              >
                <WhatsAppIcon /> Perguntar no WhatsApp
              </a>
            </div>

            <div className="border-t border-[#E8E2D9] pt-6">
              <p className="font-mono text-[11px] uppercase tracking-wider text-[#5A534C] mb-3">Compartilhar este produto</p>
              <div className="flex gap-2">
                <button type="button" onClick={shareWhatsApp} data-testid="share-whatsapp-button"
                  className="inline-flex items-center gap-2 bg-[#25D366] text-white px-5 py-2.5 rounded-full font-display text-xs font-semibold hover:opacity-90 transition-opacity">
                  <Share2 className="w-4 h-4" /> WhatsApp
                </button>
                <button type="button" onClick={copyLink} data-testid="copy-link-button"
                  className="inline-flex items-center gap-2 border border-[#D5CDC2] px-5 py-2.5 rounded-full font-display text-xs font-semibold hover:border-[#1A1816] transition-colors">
                  <Link2 className="w-4 h-4" /> Copiar Link
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Avaliações do produto */}
        {reviews.length > 0 && (
          <div className="mt-16" data-testid="product-reviews">
            <h2 className="font-display font-bold text-2xl mb-6">Quem comprou, aprovou</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {reviews.map((r) => (
                <article key={r.id} className="bg-white rounded-2xl border border-[#E8E2D9] p-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex gap-0.5">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star key={n} className={`w-4 h-4 ${n <= r.rating ? "fill-[var(--brand)] text-[var(--brand)]" : "text-[#D5CDC2]"}`} />
                      ))}
                    </div>
                    {r.verified && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full">
                        <BadgeCheck className="w-3 h-3" /> verificada
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-[#1A1816]">“{r.comment}”</p>
                  <p className="font-display font-semibold text-sm mt-3">{r.customer_name}</p>
                  {r.reply && (
                    <div className="mt-3 bg-[#F2EDE4] rounded-xl px-4 py-3">
                      <p className="text-[11px] font-mono uppercase tracking-wider text-[var(--brand)] mb-1">Resposta da Dra.Cell</p>
                      <p className="text-xs text-[#5A534C]">{r.reply}</p>
                    </div>
                  )}
                </article>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Zoom / lightbox */}
      {zoomed && photos.length > 0 && (
        <div className="fixed inset-0 z-[70] bg-black/90 flex items-center justify-center p-4" onClick={() => setZoomed(false)} data-testid="gallery-lightbox">
          <button type="button" className="absolute top-4 right-4 text-white p-2" aria-label="Fechar" onClick={() => setZoomed(false)}>
            <X className="w-7 h-7" />
          </button>
          {photos.length > 1 && (
            <>
              <button type="button" onClick={(e) => { e.stopPropagation(); prev(); }} className="absolute left-4 text-white p-2" aria-label="Anterior">
                <ChevronLeft className="w-8 h-8" />
              </button>
              <button type="button" onClick={(e) => { e.stopPropagation(); next(); }} className="absolute right-4 text-white p-2" aria-label="Próxima">
                <ChevronRight className="w-8 h-8" />
              </button>
            </>
          )}
          <img src={resolveImageUrl(photos[index])} alt={product.name} className="max-w-full max-h-full object-contain" onClick={(e) => e.stopPropagation()} />
        </div>
      )}

      <CartDrawer whatsappNumber={settings.whatsapp_number} />
    </div>
  );
}
